import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import {
  FlatJoinSplitV2MerkleTree,
  type FlatJoinSplitV2LeafInsertion,
  type FlatJoinSplitV2TreeScanRequest,
} from "./merkleIndex";
// The stable scan path: tests and callers substitute the scanner here.
import { readFlatJoinSplitV2TreeHistory } from "./treeHistory";

type SavedJournalTree = {
  version: 1;
  block: number;
  hash: string;
  root: string;
  leaves: string[];
  insertions: FlatJoinSplitV2LeafInsertion[];
};
const MAX_PERSISTED_LEAVES = 20_000;
const sameField = (a: unknown, b: unknown) => {
  try {
    return BigInt(String(a)) === BigInt(String(b));
  } catch {
    return false;
  }
};

/** Journal reconciliation needs every leaf insertion together with the
 * transaction that made it, so it keeps its own checkpoint of that public
 * evidence and continues from it on the next page load instead of re-reading
 * the pool's whole history. Before a saved prefix is used, its block is
 * re-pinned by hash, its leaves are rebuilt into the tree and checked against
 * the saved root, and every insertion must describe exactly its leaf. Any
 * mismatch, missing storage or reorg falls back to a full scan. */
/** A cold scan of a long history is done in bounded steps, each of which
 * persists its checkpoint, so a scan cut short by a read deadline resumes
 * where it stopped instead of starting over on the next check. */
const STEP_BLOCKS = 40_000;
export async function readJournalTreeHistory(
  request: Omit<FlatJoinSplitV2TreeScanRequest, "existingLeaves">,
) {
  if (
    !EVM_CONFIG.JOINSPLIT_V2.INCREMENTAL_SYNC ||
    typeof window === "undefined"
  )
    return readFlatJoinSplitV2TreeHistory(request);
  const pool = ethers.getAddress(request.pool);
  const key = `stealthos_journal_tree_v1:${EVM_CONFIG.CHAIN_ID}:${pool.toLowerCase()}:${request.treeIndex}:${request.fromBlock}`;
  let resumeFrom = request.fromBlock - 1;
  try {
    const saved = JSON.parse(
      window.localStorage?.getItem(key) || "null",
    ) as SavedJournalTree | null;
    if (
      saved &&
      Number.isSafeInteger(saved.block) &&
      saved.block >= request.fromBlock &&
      saved.block <= request.toBlock
    )
      resumeFrom = saved.block;
  } catch {
    /* unreadable checkpoint: scan from the start */
  }
  for (
    let step = resumeFrom + STEP_BLOCKS;
    step < request.toBlock;
    step += STEP_BLOCKS
  ) {
    await readJournalTreeHistoryPinned(
      { ...request, toBlock: step, expectedToBlockHash: undefined },
      key,
    );
  }
  return readJournalTreeHistoryPinned(request, key);
}
async function readJournalTreeHistoryPinned(
  request: Omit<FlatJoinSplitV2TreeScanRequest, "existingLeaves">,
  key: string,
) {
  const pool = ethers.getAddress(request.pool);
  return readFlatJoinSplitV2TreeHistory(request, {
    key,
    load: async () => {
      try {
        const raw = window.localStorage?.getItem(key);
        if (!raw || raw.length > 7_000_000) return;
        const saved = JSON.parse(raw) as SavedJournalTree;
        if (
          saved.version !== 1 ||
          !Number.isSafeInteger(saved.block) ||
          saved.block < request.fromBlock ||
          saved.block > request.toBlock ||
          !ethers.isHexString(saved.hash, 32) ||
          !ethers.isHexString(saved.root, 32) ||
          !Array.isArray(saved.leaves) ||
          !Array.isArray(saved.insertions) ||
          saved.leaves.length > MAX_PERSISTED_LEAVES ||
          saved.insertions.length !== saved.leaves.length
        )
          return;
        for (let index = 0; index < saved.insertions.length; index++) {
          const insertion = saved.insertions[index];
          if (
            !insertion ||
            insertion.leafIndex !== index ||
            insertion.treeIndex !== request.treeIndex ||
            !sameField(insertion.commitment, saved.leaves[index]) ||
            !Number.isSafeInteger(insertion.blockNumber) ||
            insertion.blockNumber < request.fromBlock ||
            insertion.blockNumber > saved.block ||
            !ethers.isHexString(insertion.blockHash, 32) ||
            !Number.isSafeInteger(insertion.transactionIndex) ||
            !Number.isSafeInteger(insertion.logIndex) ||
            (insertion.transactionHash !== undefined &&
              !ethers.isHexString(insertion.transactionHash, 32))
          )
            return;
        }
        const tree = await FlatJoinSplitV2MerkleTree.create(
          request.treeIndex,
          saved.leaves,
        );
        if (tree.root !== saved.root) return;
        return {
          block: saved.block,
          hash: saved.hash,
          value: {
            pool,
            treeIndex: request.treeIndex,
            fromBlock: request.fromBlock,
            toBlock: saved.block,
            toBlockHash: saved.hash,
            leaves: tree.getLeaves(),
            insertions: saved.insertions,
            root: tree.root,
            nextLeafIndex: tree.nextLeafIndex,
            tree,
          },
        };
      } catch {
        return;
      }
    },
    save: async (checkpoint) => {
      if (
        checkpoint.value.leaves.length > MAX_PERSISTED_LEAVES ||
        checkpoint.value.insertions.length !== checkpoint.value.leaves.length
      )
        return;
      try {
        window.localStorage?.setItem(
          key,
          JSON.stringify({
            version: 1,
            block: checkpoint.block,
            hash: checkpoint.hash,
            root: checkpoint.value.root,
            leaves: checkpoint.value.leaves,
            insertions: checkpoint.value.insertions,
          } satisfies SavedJournalTree),
        );
      } catch {
        /* Quota/private browsing affects speed only, never recovery. */
      }
    },
  });
}
